import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  AppBarColor,
  HeaderPosition,
  LocationConfiguration,
  LocationConfigurationHolder,
  useLocationConfiguration,
} from "../../LocationConfiguration";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
  InputAdornment,
  FormControl,
  TextField,
  AppBarOwnProps,
  useMediaQuery,
  DialogTitle,
  Paper,
  Toolbar,
} from "@mui/material";
import { useLanguage } from "../../Language";
import { Button } from "@mui/material";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";

import { Stack } from "@mui/material";
import {
  Controller,
  useForm,
  SubmitHandler,
  FormProvider,
} from "react-hook-form";
import { getContrastRatio, blend } from "@mui/system";
import Divider from "@mui/material/Divider";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close"; // Import CloseIcon
import { ThemePreview } from "../../ThemePreview";
import { drawerWidth } from "../account/MyAccount";
import HeaderStyleControls from "../../HeaderStyleControls";
import { LogoSelector } from "./LogoSelector";
import { FormTextField } from "@tlc/forms/FormTextField";
import { DisplaySettings } from "@tlc/ls2pac/dto/DisplaySettings";

export interface IFormThemeValues
  extends Pick<
      DisplaySettings,
      | "primaryColor"
      | "secondaryColor"
      | "appBarColor"
      | "mainPageLogoImageId"
      | "headerPosition"
      | "showHeaderOnMobile"
      | "mainPageLogoImageAltText"
    >,
    Pick<LocationConfiguration, "libraryName"> {}

export const AppBarThemeColor: Record<AppBarColor, AppBarOwnProps["color"]> = {
  [AppBarColor.Default]: "default",
  [AppBarColor.Primary]: "primary",
  [AppBarColor.Secondary]: "secondary",
};

export const AppBarSxBgcolor: Record<AppBarColor, string> = {
  [AppBarColor.Default]: "var(--mui-palette-AppBar-defaultBg)",
  [AppBarColor.Primary]: "primary.main",
  [AppBarColor.Secondary]: "secondary.main",
};

enum Severity {
  Success = "success",
  Warn = "warn",
  Error = "error",
}

export const BrandingPage: React.FC = () => {
  const theme = useTheme();
  const { locationConfiguration, saveTheme } = useLocationConfiguration();
  // Do we need defaults from locationConfiguration here for header config
  const {
    primaryColor: initialPrimaryColor,
    secondaryColor: initialSecondaryColor,
    defaultPrimaryColor,
    defaultSecondaryColor,
    appBarColor: initialAppBarColor,
    mainPageLogoImageId,
    headerPosition: initialHeaderPosition,
    showHeaderOnMobile: initialShowHeaderOnMobile,
    mainPageLogoImageAltText,
  } = locationConfiguration.pacSettings.displaySettings;

  const { translate } = useLanguage();

  const getPrimarySeverity = (hexValue: string): Severity => {
    const contrastRatio = getContrastRatio(
      hexValue,
      theme.palette.background.paper,
    );

    if (contrastRatio >= 3 && contrastRatio < 4.5) {
      return Severity.Warn;
    } else if (contrastRatio < 3) {
      return Severity.Error;
    } else {
      return Severity.Success;
    }
  };

  const getSecondarySeverity = (hexValue: string): Severity => {
    const selectedToggleColor = getSelectedToggleButtonBackground(hexValue);
    const contrastRatio = getContrastRatio(hexValue, selectedToggleColor);

    if (contrastRatio >= 3 && contrastRatio < 4.5) {
      return Severity.Warn;
    } else if (contrastRatio < 3) {
      return Severity.Error;
    } else {
      return Severity.Success;
    }
  };

  function getSelectedToggleButtonBackground(
    selectedColor: string,
    opacity: number = theme.palette.action.selectedOpacity,
    gamma: number = 1.0,
  ): string | null {
    // Apply gamma correction and blend colors
    let targetColor = blend(
      theme.palette.background.paper,
      selectedColor,
      opacity,
      gamma,
    );

    return targetColor;
  }

  const [primarySeverity, setPrimarySeverity] = useState<Severity>(
    getPrimarySeverity(theme.palette.primary.main),
  );
  const [secondarySeverity, setSecondarySeverity] = useState<Severity>(
    getSecondarySeverity(theme.palette.secondary.main),
  );

  const [showImageAltField, setShowImageAltField] = useState<boolean>(
    !!mainPageLogoImageId,
  );

  const methods = useForm<IFormThemeValues>({
    defaultValues: {
      primaryColor: initialPrimaryColor,
      secondaryColor: initialSecondaryColor,
      appBarColor: initialAppBarColor,
      mainPageLogoImageId: mainPageLogoImageId,
      mainPageLogoImageAltText: mainPageLogoImageAltText,
      headerPosition: initialHeaderPosition,
      showHeaderOnMobile: initialShowHeaderOnMobile,
      libraryName: locationConfiguration.libraryName,
    },
    mode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    resetField,
    getValues,
    setValue,
    formState: { errors },
    trigger,
  } = methods;

  const [saveThemeError, setSaveThemeError] = useState<boolean>(false);

  const onSubmit: SubmitHandler<IFormThemeValues> = async (data) => {
    setSaveThemeError(false);

    // clear alt-text on submit if there is no image applied
    if (!showImageAltField) {
      data.mainPageLogoImageAltText = "";
    }

    console.log("Form Payload", data);
    try {
      const success = await saveTheme(data);
      if (success === true) {
        SuccessMessageControl.show(translate("saveThemeSuccess"));
        reset(data);
      } else {
        //Todo: show generic error message
        setSaveThemeError(true);
      }
    } catch (error) {
      //TODO: show generic error message
      setSaveThemeError(true);
      console.error("Failed to save theme: ", error);
    }
  };

  const onRestore = async () => {
    // this does not work. you need to merge defaults with getValues or reset fields individually.
    // reset(defaults);
    // resetField("primaryColor");
    // resetField("secondaryColor");
    // resetField("appBarColor");
    // or
    // const {primaryColor, secondaryColor, appBarColor, ...values} = getValues();
    // reset({...defaults, ...values});
    // however, in this case we want to "change" to the LS2Pac defaults not reset the field values.
    setValue("primaryColor", defaultPrimaryColor);
    setValue("secondaryColor", defaultSecondaryColor);
    setValue("appBarColor", AppBarColor.Primary);
    setPrimarySeverity(getPrimarySeverity(defaultPrimaryColor));
    setSecondarySeverity(getSecondarySeverity(defaultSecondaryColor));
  };

  const onReset = () => {
    reset();
    setSaveThemeError(false);
    setPrimarySeverity(getPrimarySeverity(theme.palette.primary.main));
    setSecondarySeverity(getSecondarySeverity(theme.palette.secondary.main));
  };

  const getAdornment = (severity: "success" | "warn" | "error" | undefined) => {
    return (
      <InputAdornment position="end">
        {severity === Severity.Warn && <WarningIcon color="warning" />}
        {severity === Severity.Error && <ErrorIcon color="error" />}
        {severity === Severity.Success && <CheckCircleIcon color="success" />}
        {/* Fallback for empty space */}
        {!severity && (
          <Box
            sx={{
              width: 24,
              height: 24,
            }}
          />
        )}{" "}
      </InputAdornment>
    );
  };

  const [primaryColor, setPrimaryColor] = useState<string>(initialPrimaryColor);
  const [secondaryColor, setSecondaryColor] = useState<string>(
    initialSecondaryColor,
  );
  const [appBarColor, setAppBarColor] =
    useState<AppBarColor>(initialAppBarColor);
  const [selectedLogoImageId, setSelectedLogoImageId] = useState<string | null>(
    mainPageLogoImageId,
  );
  const [libraryName, setLibraryName] = useState<string>(
    locationConfiguration.libraryName,
  );

  const [selectedHeaderPosition, setSelectedHeaderPosition] =
    useState<HeaderPosition>(initialHeaderPosition);

  //TODO  we should replace this effect with useWatchWithCallback once feature/ILSFP-5163 is integrated
  useEffect(() => {
    let timeoutHandler: number;

    const { unsubscribe } = watch(
      ({
        primaryColor,
        secondaryColor,
        libraryName,
        appBarColor,
        mainPageLogoImageId,
        headerPosition,
      }) => {
        if (timeoutHandler) {
          clearTimeout(timeoutHandler);
        }

        // debounce for any inputs that could change rapidly
        timeoutHandler = window.setTimeout(() => {
          setPrimaryColor(primaryColor);
          setSecondaryColor(secondaryColor);
          setLibraryName(libraryName);
        }, 50);

        // we don't want to debounce this selection
        setAppBarColor(appBarColor);
        setSelectedLogoImageId(mainPageLogoImageId);
        // Show the Altfield if there is a value
        setShowImageAltField(!!mainPageLogoImageId);
        setSelectedHeaderPosition(headerPosition);
      },
    );
    return () => {
      unsubscribe();
      if (timeoutHandler) {
        clearTimeout(timeoutHandler);
      }
    };
  }, [watch]);

  // Replace Watchers with this useEffect
  useEffect(() => {
    // re-trigger validation dependent on selectedLogoImageId
    // for fields that currently have errors
    if (selectedLogoImageId) {
      if (errors.libraryName?.type === "required") {
        trigger("libraryName");
      }
    } else {
      if (errors.mainPageLogoImageAltText?.type === "required") {
        // TODO clear the alt text?
        trigger("mainPageLogoImageAltText");
      }
    }
  }, [selectedLogoImageId, trigger]);

  const LogoCardSxBgcolor: Record<AppBarColor, string> = useMemo(() => {
    return {
      [AppBarColor.Default]: "var(--mui-palette-AppBar-defaultBg)",
      [AppBarColor.Primary]: primaryColor,
      [AppBarColor.Secondary]: secondaryColor,
    };
  }, [primaryColor, secondaryColor]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const themePreviewWidth = "360px";
  const controlContainerWidth = "360px";
  const gutterWidth = "24px";

  const sideNavVisible = !useMediaQuery(theme.breakpoints.down("md"));
  const cutoff =
    parseFloat(themePreviewWidth) +
    parseFloat(controlContainerWidth) +
    (sideNavVisible ? parseFloat(drawerWidth) + parseFloat(gutterWidth) : 0);
  const displayFullPreview = useMediaQuery(`(min-width:${cutoff}px)`);

  const [open, setOpen] = React.useState(false);

  const onClosePreview = () => setOpen(false);
  const onShowPreview = () => setOpen(true);

  return (
    <>
      <Typography variant="h5">{translate("branding")}</Typography>
      <Typography sx={{ mb: 1, mt: 2 }} variant="h6">
        {translate("logoSelectionHeader")}
      </Typography>
      {/* GOTO: Theme form begins here */}
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <LogoSelector<IFormThemeValues>
            name="mainPageLogoImageId"
            bgcolor={LogoCardSxBgcolor[appBarColor]}
          />
          <Divider component="div" role="presentation" />
          {/* TODO language translations */}
          <Stack
            direction={"row"}
            spacing={1}
            sx={{
              justifyContent: "flex-start",
              alignItems: "left",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                maxWidth: isSmallScreen ? "100%" : controlContainerWidth,
                justifyContent: "flex-start",
              }}
            >
              {/* GOTO: Header Controls */}
              <Stack direction="row" spacing={2}>
                <Stack
                  direction="column"
                  spacing={2}
                  sx={{
                    maxWidth: isSmallScreen ? "100%" : controlContainerWidth,
                    justifyContent: "flex-start",
                    pt: 3,
                    // pr: 3,
                    pb: 1,
                  }}
                >
                  {/* GOTO Header Controls */}
                  <Box
                    sx={{
                      maxWidth: isSmallScreen ? "100%" : controlContainerWidth,
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        pb: 1,
                        textAlign: "left",
                      }}
                    >
                      {translate("headerSettingsLabel")}
                    </Typography>
                    <HeaderStyleControls />
                    <Stack spacing={3}>
                      <FormTextField<IFormThemeValues>
                        name={"libraryName"}
                        label={translate("libraryNameToken")}
                        maxLength={255}
                        options={{
                          required: !selectedLogoImageId
                            ? translate("libraryNameRequiredMessage")
                            : false,
                        }}
                      />
                      {showImageAltField && (
                        <FormTextField<IFormThemeValues>
                          name="mainPageLogoImageAltText"
                          label={translate("LogoAltTextToken")}
                          maxLength={255}
                          options={{
                            required:
                              selectedLogoImageId &&
                              (libraryName || "").length === 0
                                ? translate("headerAltTextRequiredMessage")
                                : false,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      maxWidth: isSmallScreen ? "100%" : controlContainerWidth,
                      justifyContent: "flex-start",
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        pr: 3,
                        pb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "left",
                        }}
                      >
                        {translate("themeColorsHeading")}
                      </Typography>{" "}
                      <Button
                        type="button"
                        color="primary"
                        sx={{
                          whiteSpace: "nowrap",
                          width: isSmallScreen ? "100%" : "auto",
                          flexShrink: 0,
                        }}
                        onClick={onRestore}
                      >
                        {translate("restoreThemeText")}
                      </Button>
                    </Stack>
                    <Stack
                      direction="column"
                      sx={{
                        gap: 3,
                        width: controlContainerWidth,
                      }}
                    >
                      {saveThemeError && (
                        <Alert severity="error">
                          {translate("saveThemeFailure")}
                        </Alert>
                      )}
                      <FormControl>
                        <InputLabel id="app-bar-color-selector-label">
                          {translate("appBarColorLabel")}
                        </InputLabel>
                        <Controller
                          name={"appBarColor"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <Select
                              labelId="app-bar-color-selector-label"
                              {...field}
                              label={translate("appBarColorLabel")}
                              sx={{
                                // minWidth: 128,
                                mb: 4,
                              }}
                              inputProps={{
                                "aria-labelledby":
                                  "app-bar-color-selector-label",
                              }}
                            >
                              <MenuItem value={AppBarColor.Primary}>
                                {translate("dropDownPrimary")}
                              </MenuItem>
                              <MenuItem value={AppBarColor.Secondary}>
                                {translate("dropDownSecondary")}
                              </MenuItem>
                              <MenuItem value={AppBarColor.Default}>
                                {translate("dropDownDefault")}
                              </MenuItem>
                            </Select>
                          )}
                        ></Controller>
                      </FormControl>

                      <Controller
                        name={"primaryColor"}
                        control={control}
                        rules={{
                          required: translate("isRequired", {
                            LABEL: translate("primaryColorLabel"),
                          }),
                          validate: (value) => {
                            const primarySeverity = getPrimarySeverity(value);
                            setPrimarySeverity(primarySeverity);

                            if (primarySeverity === Severity.Warn) {
                              return true;
                            } else if (primarySeverity === Severity.Error) {
                              return translate("minimumContrastRequired");
                            } else {
                              return true;
                            }
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            type="color"
                            {...field}
                            label={translate("primaryColorLabel")}
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error?.message ||
                              (primarySeverity === Severity.Warn
                                ? translate("minimumContrastAchieved")
                                : " ")
                            }
                            slotProps={{
                              input: {
                                endAdornment: getAdornment(primarySeverity),
                              },
                            }}
                          />
                        )}
                      />

                      <Controller
                        name="secondaryColor"
                        control={control}
                        rules={{
                          required: translate("isRequired", {
                            LABEL: translate("secondaryColorLabel"),
                          }),
                          validate: (value) => {
                            const secondarySeverity =
                              getSecondarySeverity(value);
                            setSecondarySeverity(secondarySeverity);

                            if (secondarySeverity === "warn") {
                              return true;
                            } else if (secondarySeverity === "error") {
                              return translate("minimumContrastRequired");
                            } else {
                              return true;
                            }
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            type="color"
                            {...field}
                            label={translate("secondaryColorLabel")}
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error?.message ||
                              (secondarySeverity === "warn"
                                ? translate("minimumContrastAchieved")
                                : " ")
                            }
                            slotProps={{
                              input: {
                                endAdornment: getAdornment(secondarySeverity),
                              },
                            }}
                          />
                        )}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
              {/* Left align this in the parent stack */}
              <Stack
                direction="row"
                sx={{
                  gap: 1,
                  width: "100%",

                  flexWrap: {
                    xs: "wrap",
                  },

                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                }}
              >
                {!displayFullPreview && (
                  <Button
                    type="button"
                    color="primary"
                    sx={{
                      whiteSpace: "nowrap",
                      width: "100%",
                      flexShrink: 0,
                    }}
                    onClick={onShowPreview}
                  >
                    {translate("previewthemecomponent")}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    whiteSpace: "nowrap",
                    width: isSmallScreen ? "100%" : "auto",
                    flexShrink: 0,
                  }}
                >
                  {translate("applyThemeButtonText")}
                </Button>
                <Button
                  type="button"
                  color="primary"
                  sx={{
                    whiteSpace: "nowrap",
                    width: isSmallScreen ? "100%" : "auto",
                    flexShrink: 0,
                  }}
                  onClick={onReset}
                >
                  {translate("cancel")}
                </Button>
              </Stack>
            </Box>
            {displayFullPreview && (
              <Stack
                direction={"column"}
                sx={{
                  gap: 1,
                  padding: 3,
                }}
              >
                <Typography variant="h6">
                  {translate("previewthemecomponent")}
                </Typography>
                <Paper
                  sx={{
                    padding: 2,
                    width: { themePreviewWidth },
                  }}
                >
                  <ThemePreview
                    selectedPrimaryColor={primaryColor}
                    selectedSecondaryColor={secondaryColor}
                    selectedAppBarColor={appBarColor}
                    selectedLogoImageId={selectedLogoImageId}
                    libraryName={libraryName}
                    headerPosition={selectedHeaderPosition}
                  />
                </Paper>
              </Stack>
            )}
          </Stack>
        </form>
      </FormProvider>
      <Dialog
        open={open}
        fullWidth
        fullScreen={isSmallScreen}
        aria-labelledby={translate("previewthemecomponent")}
        aria-describedby={translate("previewthemecomponent")}
        onClose={onClosePreview}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClosePreview}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle align="left">
            {translate("previewthemecomponent")}
          </DialogTitle>
        </Toolbar>
      </Dialog>
      <Dialog
        open={open}
        fullWidth
        fullScreen={isSmallScreen}
        aria-labelledby={translate("previewthemecomponent")}
        aria-describedby={translate("previewthemecomponent")}
        onClose={onClosePreview}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClosePreview}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle align="left">
            {translate("previewthemecomponent")}
          </DialogTitle>
        </Toolbar>

        <DialogContent>
          <ThemePreview
            selectedPrimaryColor={primaryColor}
            selectedSecondaryColor={secondaryColor}
            selectedAppBarColor={appBarColor}
            selectedLogoImageId={selectedLogoImageId}
            libraryName={libraryName}
            headerPosition={selectedHeaderPosition}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
